import type { Permission } from '@dnx/domain';
import { PageHeader, Tabs } from '@dnx/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { meQuery } from '../lib/session.js';

export const SETTINGS_TABS = [
  { path: 'equipe', label: 'Equipe', permission: 'identity.member.manage' },
  { path: 'marca', label: 'Marca', permission: 'tenancy.branding.write' },
  { path: 'unidade', label: 'Unidade', permission: 'tenancy.unit.manage' },
  { path: 'etiquetas', label: 'Etiquetas', permission: 'crm.settings.manage' },
  { path: 'canais', label: 'Canais', permission: 'inbox.settings.manage' },
  { path: 'respostas-rapidas', label: 'Respostas rápidas', permission: 'inbox.settings.manage' },
  { path: 'distribuicao', label: 'Distribuição de leads', permission: 'crm.settings.manage' },
  { path: 'formularios', label: 'Formulários', permission: 'crm.settings.manage' },
  { path: 'conversoes', label: 'Conversões', permission: 'crm.settings.manage' },
  { path: 'precos', label: 'Preços', permission: 'billing.price.manage' },
  { path: 'pagamentos', label: 'Pagamentos', permission: 'billing.price.manage' },
  { path: 'documentos', label: 'Documentos', permission: 'documents.template.manage' },
  { path: 'pos-operatorio', label: 'Pós-operatório', permission: 'documents.template.manage' },
] as const satisfies readonly { path: string; label: string; permission: Permission }[];
export type SettingsTab = (typeof SETTINGS_TABS)[number]['path'];

export const allowedSettingsTabs = (permissions: readonly string[]) => SETTINGS_TABS.filter((t) => permissions.includes(t.permission));

/** Configurações da clínica: uma página, uma aba por assunto (só as que o papel permite). */
export function SettingsLayout() {
  const { data: me } = useSuspenseQuery(meQuery);
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const tabs = allowedSettingsTabs(me.permissions);
  const current = tabs.find((t) => path.endsWith(`/${t.path}`));
  if (!tabs.length) return <Navigate to="/" />;
  // durante a saída para outra tela o endereço já mudou: não redirecionar de volta para uma aba
  if (!path.startsWith('/configuracoes')) return null;
  if (!current) return <Navigate to={`/configuracoes/${tabs[0]!.path}`} replace />;
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Configurações" description="Como a clínica funciona no sistema: equipe, marca e ferramentas do atendimento." />
      <Tabs className="mt-8" value={current.path} tabs={tabs.map((t) => ({ value: t.path, label: t.label }))}
        onChange={(v) => void navigate({ to: `/configuracoes/${v}` })} />
      <div className="mt-8" role="tabpanel"><Outlet /></div>
    </div>
  );
}

/** Cabeçalho de cada aba de configurações. */
export function SectionHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold text-fg">{title}</h2>
        {description && <p className="mt-1 max-w-2xl text-sm text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}
